// @/components/Ajuda/Montador/lib/defaultParametros.js
// Fonte única dos parâmetros + defaults + persistência (localStorage)

// === Defaults de alturas (unificado) ===
export const DEFAULT_SIMPLE_HEIGHT  = 1.2;                 // vertical, gate, plank, wall
export const DEFAULT_OXER_HEIGHTS   = [0.60, 0.80];
export const DEFAULT_TRIPLE_HEIGHTS = [0.60, 0.80, 1.00];

// === Versão / chave de armazenamento ===
export const PARAMS_VERSION = 1;
export const PARAMS_STORAGE_KEY = `montador.params.v${PARAMS_VERSION}`;

const clone = (obj) =>
  (typeof structuredClone === 'function') ? structuredClone(obj) : JSON.parse(JSON.stringify(obj));

// Gerador de params padrão (sem compartilhar referências)
export function getDefaultParams() {
  return {
    version: PARAMS_VERSION,

    arena: {
      w: 60, h: 30, scale: 16, snapGrid: 0.5,
      freeShowHandles: true, freeConfine: false,
      applyNow: false,
    },

    visual: {
      showRulers: true, rulersInsetPx: 12,
      showDistanceLabels: true, decimals: 2,
      preferGlobalRMin: true,
    },

    obstacles: {
      // Simples (uma barra)
      vertical: { wm: 3.75, hm: DEFAULT_SIMPLE_HEIGHT, dir: 1, barThickness: 0.10, heights: [DEFAULT_SIMPLE_HEIGHT] },
      gate:     { wm: 3.75, hm: DEFAULT_SIMPLE_HEIGHT, dir: 1, barThickness: 0.15, heights: [DEFAULT_SIMPLE_HEIGHT] },
      plank:    { wm: 3.75, hm: DEFAULT_SIMPLE_HEIGHT, dir: 1, barThickness: 0.15, heights: [DEFAULT_SIMPLE_HEIGHT] },
      wall:     { wm: 3.75, hm: DEFAULT_SIMPLE_HEIGHT, dir: 1, barThickness: 0.20, heights: [DEFAULT_SIMPLE_HEIGHT] },

      // Múltiplas barras
      oxer: {
        wm: 3.75, hm: 1.2, dir: 1,
        thicknesses: [0.10, 0.10],
        gaps: [0.60],
        barHeights: [...DEFAULT_OXER_HEIGHTS],
      },
      tripleBar: {
        wm: 3.75, hm: 1.6, dir: 1,
        thicknesses: [0.10, 0.10, 0.10],
        gaps: [0.50, 0.60],
        barHeights: [...DEFAULT_TRIPLE_HEIGHTS],
      },

      start:  { wm: 4.75, hm: 2.0, dir: 1, barThickness: 0.10, minOutStraight: 6 },
      finish: { wm: 4.75, hm: 2.0, dir: 1, barThickness: 0.10, minInStraight: 6 },
    },

    combos: { defaultCentro: 7.5, centerOnCreate: true },
    path:   { pathTension: 0.35, curveSmoothness: 0.5, exitStrides: 2.0, entryStrides: 1.3, rMin: 6.0, clearance: 1.5 },
  };
}

// === Normalização e preenchimento de faltantes ===
export function normalizeParamsInPlace(p) {
  p.obstacles = p.obstacles || {};

  // Sinônimos legados
  if (p.obstacles.muro && !p.obstacles.wall)        p.obstacles.wall = clone(p.obstacles.muro);
  if (p.obstacles.triple && !p.obstacles.tripleBar) p.obstacles.tripleBar = clone(p.obstacles.triple);

  const SINGLE_BAR_TYPES = ['vertical','gate','plank','wall'];

  for (const [tipo, o0] of Object.entries(p.obstacles)) {
    const o = o0 || {};

    // Consolidar alturas → heights[]
    if (Array.isArray(o.barHeights) && !Array.isArray(o.heights))     o.heights = o.barHeights.slice();
    if (Array.isArray(o?.bars?.heights) && !Array.isArray(o.heights)) o.heights = o.bars.heights.slice();

    // Limpa aliases legados
    delete o.barHeights;
    if (o.bars) delete o.bars.heights;
    delete o.alturas;
    delete o.heightsStr;

    // Tipos simples: derivar a 1ª altura a partir de hm, se necessário
    if ((!Array.isArray(o.heights) || o.heights.length === 0) && SINGLE_BAR_TYPES.includes(tipo) && Number.isFinite(+o.hm)) {
      o.heights = [Number(o.hm)];
    }

    p.obstacles[tipo] = o;
  }

  return p;
}

/** Preenche ausentes a partir do source (sem sobrescrever definidos) + normaliza */
export function applyDefaults(target, source = getDefaultParams()) {
  if (!target || typeof target !== 'object') return clone(source);
  fillMissing(target, source);
  normalizeParamsInPlace(target);
  return target;
}

function fillMissing(t, s) {
  if (!s || typeof s !== 'object') return;
  for (const k of Object.keys(s)) {
    const sv = s[k], tv = t[k];
    if (tv == null) { t[k] = clone(sv); continue; }
    if (Array.isArray(sv)) { if (!Array.isArray(tv) || tv.length === 0) t[k] = clone(sv); continue; }
    if (typeof sv === 'object') { if (typeof tv !== 'object') t[k] = clone(sv); else fillMissing(tv, sv); }
  }
}

// === Persistência (localStorage) ===
function sanitizeForSave(p) {
  const out = clone(p);
  if (out?.arena) delete out.arena.applyNow; // não persistir toggle momentâneo do modal
  normalizeParamsInPlace(out);
  return out;
}

export function saveParamsToLocal(p) {
  try {
    const data = sanitizeForSave(p);
    localStorage.setItem(PARAMS_STORAGE_KEY, JSON.stringify({ v: PARAMS_VERSION, data }));
  } catch (e) {
    console.warn('[params] save local failed:', e);
  }
}

export function loadParamsFromLocal() {
  try {
    const raw = localStorage.getItem(PARAMS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const data = parsed.data;
    if (!data || typeof data !== 'object') return null;

    // Sempre normaliza e completa faltantes
    return applyDefaults(data);
  } catch (e) {
    console.warn('[params] load local failed:', e);
    return null;
  }
}

export function clearParamsFromLocal() {
  try {
    localStorage.removeItem(PARAMS_STORAGE_KEY)
  } catch (e) {
    console.warn('[params] clear local failed:', e)
  }
}
